import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, Plus, Calendar, Building2, FileText, ArrowRight, Eye, Ban } from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';
import { api } from '@/api';
import { toast } from 'sonner';

export default function ShopDrawingsTab({
  projectId,
  shopDrawings,
  selectedShopDrawing,
  setSelectedShopDrawing,
  project,
  onRefresh
}) {
  const navigate = useNavigate();
  const [voidingId, setVoidingId] = useState(null);

  const handleVoid = async (e, sd) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to void "${sd.package_ref || sd.reference}"? This action cannot be undone.`)) return;
    
    setVoidingId(sd.id);
    try {
      await api.patch(`/shop-drawings/${sd.id}/void`);
      toast.success('Package voided successfully');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void package');
    } finally {
      setVoidingId(null);
    }
  };

  const activeDrawings = shopDrawings.filter(sd => sd.status !== 'VOIDED');
  const voidedDrawings = shopDrawings.filter(sd => sd.status === 'VOIDED');

  return (
    <div className="space-y-6">
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="text-slate-600 hover:text-slate-900"
        data-testid="shop-drawings-back-button"
      >
        ← Back to Dashboard
      </Button>
      
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-barlow font-semibold uppercase">Shop Drawings</h2>
          <p className="text-sm text-slate-500 mt-1">{activeDrawings.length} active packages{voidedDrawings.length > 0 ? `, ${voidedDrawings.length} voided` : ''}</p>
        </div>
        <div className="flex gap-2">
          <PrintScheduleButton moduleType="ShopDrawing" data={shopDrawings} project={project} />
          <Button 
            onClick={() => navigate(`/projects/${projectId}/shop-drawings?action=new`)}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Package
          </Button>
        </div>
      </div>
      
      {shopDrawings.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No shop drawing packages yet</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate(`/projects/${projectId}/shop-drawings?action=new`)}
            >
              Create First Package
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {shopDrawings.map(sd => (
            <Card 
              key={sd.id} 
              className={`cursor-pointer transition-colors ${
                sd.status === 'VOIDED' 
                  ? 'opacity-50 border-dashed border-slate-300 bg-slate-50' 
                  : selectedShopDrawing?.id === sd.id 
                    ? 'border-slate-900 ring-1 ring-slate-900 hover:border-slate-400' 
                    : 'hover:border-slate-400'
              }`}
              onClick={() => sd.status !== 'VOIDED' && setSelectedShopDrawing(selectedShopDrawing?.id === sd.id ? null : sd)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-mono font-bold ${sd.status === 'VOIDED' ? 'line-through text-slate-400' : ''}`}>
                        {sd.package_ref || sd.reference}
                      </span>
                      <Badge className={
                        sd.status === 'VOIDED' ? 'bg-slate-200 text-slate-500' :
                        sd.status === 'Approved' ? 'bg-green-100 text-green-700' : 
                        sd.status === 'Submitted to Consultant' ? 'bg-blue-100 text-blue-700' : 
                        sd.status === 'Under Consultant Review' ? 'bg-purple-100 text-purple-700' :
                        sd.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {sd.status === 'VOIDED' ? 'Voided' : sd.status}
                      </Badge>
                      {sd.current_cycle > 1 && sd.status !== 'VOIDED' && <Badge variant="outline">Rev {sd.current_cycle}</Badge>}
                    </div>
                    <p className={`text-sm font-medium ${sd.status === 'VOIDED' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                      {sd.title || sd.package_title}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span><Building2 className="h-3 w-3 inline mr-1" />{sd.subcontractor_name || sd.subcontractor || '-'}</span>
                      <span><FileText className="h-3 w-3 inline mr-1" />{sd.drawings_count || sd.drawings?.length || 0} drawings</span>
                      <span><Calendar className="h-3 w-3 inline mr-1" />{sd.created_at ? new Date(sd.created_at).toLocaleDateString() : '-'}</span>
                      {sd.status === 'VOIDED' && sd.voided_by_name && (
                        <span className="text-slate-400">Voided by {sd.voided_by_name}</span>
                      )}
                    </div>
                  </div>
                  {sd.status !== 'VOIDED' && (
                    <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedShopDrawing?.id === sd.id ? 'rotate-90' : ''}`} />
                  )}
                </div>
                
                {selectedShopDrawing?.id === sd.id && sd.status !== 'VOIDED' && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Discipline</p>
                        <p className="font-medium">{sd.discipline || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Current Cycle</p>
                        <p className="font-medium">{sd.current_cycle || 1}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Consultant</p>
                        <p className="font-medium">{sd.consultant_name || sd.assigned_consultant || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Days in Review</p>
                        <p className="font-medium">{sd.days_in_review || '-'}</p>
                      </div>
                    </div>
                    {sd.description && (
                      <div>
                        <p className="text-slate-500 text-sm">Description</p>
                        <p className="text-sm">{sd.description}</p>
                      </div>
                    )}
                    {sd.consultant_comments && (
                      <div>
                        <p className="text-slate-500 text-sm">Consultant Comments</p>
                        <p className="text-sm italic">"{sd.consultant_comments}"</p>
                      </div>
                    )}
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/shop-drawings/${sd.id}`); }}>
                        <Eye className="h-4 w-4 mr-1" />
                        View Full Details
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={(e) => handleVoid(e, sd)}
                        disabled={voidingId === sd.id}
                        data-testid={`void-package-${sd.id}`}
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        {voidingId === sd.id ? 'Voiding...' : 'Void Package'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
